import React, { useEffect, useRef } from "react";

import Modal from "./modal";
import { Listbox } from "@headlessui/react";
import {
  ErrorBanner,
  Button,
  cn,
  inputClasses,
  dropdownItemClasses,
  dropdownItemActiveClasses,
  dropdownItemSelectedClasses,
  PortaledListboxOptions,
} from "../../ui";

const FONT_SIZE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "11", value: 11 },
  { label: "12", value: 12 },
  { label: "14", value: 14 },
  { label: "16", value: 16 },
  { label: "18", value: 18 },
];

const INDENT_OPTIONS = [
  { label: "Spaces", value: "SPACES" },
  { label: "Tabs", value: "TABS" },
];

const SPACE_SIZE_OPTIONS = [
  { label: "2", value: 2 },
  { label: "4", value: 4 },
];

function SettingsListbox({
  id,
  label,
  value,
  displayValue,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string | number;
  displayValue: string | number;
  options: { label: string; value: string | number }[];
  onChange: (val: string | number) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="min-w-0 flex-1">
      <label
        className="mb-1 block text-sm font-medium text-neutral-700"
        htmlFor={id}
      >
        {label}
      </label>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              id={id}
              ref={buttonRef}
              className={cn(
                inputClasses,
                "relative w-full cursor-default py-2 pl-3 pr-8 text-left text-sm"
              )}
            >
              <span className="block truncate">{displayValue}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <svg
                  className="h-4 w-4 text-neutral-400"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M6 8l4 4 4-4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Listbox.Button>
            <PortaledListboxOptions
              open={open}
              buttonRef={buttonRef}
              maxHeightPx={192}
            >
              {options.map((item) => (
                <Listbox.Option key={String(item.value)} value={item.value}>
                  {({ active, selected }) => (
                    <div
                      className={cn(
                        dropdownItemClasses,
                        "cursor-pointer",
                        active && dropdownItemActiveClasses,
                        selected && !active && dropdownItemSelectedClasses
                      )}
                    >
                      {item.label}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </PortaledListboxOptions>
          </>
        )}
      </Listbox>
    </div>
  );
}

function EditorSettingModal({
  formError,
  intialValues,
  toggle,
  isOpen,
  handleChangeInput,
  htmlEditorRef,
  cssEditorRef,
}) {
  useEffect(() => {
    if (htmlEditorRef.current && cssEditorRef.current) {
      htmlEditorRef.current.getModel()?.updateOptions({
        indentSize: intialValues?.indent_size ? intialValues?.indent_size : 2,
      });
      cssEditorRef.current.getModel()?.updateOptions({
        indentSize: intialValues?.indent_size ? intialValues?.indent_size : 2,
      });
    }
  }, [intialValues]);

  return (
    <Modal
      toggle={toggle}
      isOpen={isOpen}
      size="sm:max-w-sm"
      closeButton
      overflowVisible
    >
      <div className="text-left">
        <div className="sm:flex sm:items-start">
          <h3
            className="text-base font-semibold tracking-tight text-neutral-900"
            id="modal-headline"
          >
            Editor Settings
          </h3>
        </div>
        <div className="mt-5 flex flex-col gap-3">
          <div className="flex items-center gap-4 w-full">
            <label className="relative inline-block w-[50px] h-[24px]">
              <input
                type="checkbox"
                className="h-0 w-0 opacity-0"
                checked={intialValues?.word_wrap || false}
                onChange={() => {
                  handleChangeInput("word_wrap", !intialValues?.word_wrap);
                }}
                id="word_wrap"
              />
              <button
                onClick={() => {
                  handleChangeInput("word_wrap", !intialValues?.word_wrap);
                }}
                className="slider round shadow cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
              />
            </label>
            <label
              className="cursor-pointer select-none text-sm text-neutral-700"
              htmlFor="word_wrap"
            >
              Word Wrap
            </label>
          </div>
          <div className="flex items-center gap-4 w-full">
            <label className="relative inline-block w-[50px] h-[24px]">
              <input
                checked={intialValues?.line_numbers || false}
                id="line_numbers"
                type="checkbox"
                onChange={() => {
                  handleChangeInput(
                    "line_numbers",
                    !intialValues?.line_numbers
                  );
                }}
                className="h-0 w-0 opacity-0"
              />
              <button
                onClick={() => {
                  handleChangeInput(
                    "line_numbers",
                    !intialValues?.line_numbers
                  );
                }}
                className="slider round shadow cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
              />
            </label>
            <label
              className="cursor-pointer select-none text-sm text-neutral-700"
              htmlFor="line_numbers"
            >
              Line Numbers
            </label>
          </div>
          <div className="flex items-center gap-4 w-full">
            <label className="relative inline-block w-[50px] h-[24px]">
              <input
                type="checkbox"
                className="h-0 w-0 opacity-0"
                checked={intialValues?.auto_complete_html || false}
                onChange={() => {
                  handleChangeInput(
                    "auto_complete_html",
                    !intialValues?.auto_complete_html
                  );
                }}
                id="auto_complete_html"
              />
              <button
                onClick={() => {
                  handleChangeInput(
                    "auto_complete_html",
                    !intialValues?.auto_complete_html
                  );
                }}
                className="slider round shadow cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
              />
            </label>
            <label
              className="cursor-pointer select-none text-sm text-neutral-700"
              htmlFor="auto_complete_html"
            >
              Auto-Complete HTML
            </label>
          </div>
          <div className="flex items-center gap-4 w-full">
            <label className="relative inline-block w-[50px] h-[24px]">
              <input
                type="checkbox"
                className="h-0 w-0 opacity-0"
                checked={intialValues?.format_on_save || false}
                onChange={() => {
                  handleChangeInput(
                    "format_on_save",
                    !intialValues?.format_on_save
                  );
                }}
                id="format_on_save"
              />
              <button
                onClick={() => {
                  handleChangeInput(
                    "format_on_save",
                    !intialValues?.format_on_save
                  );
                }}
                className="slider round shadow cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
              />
            </label>
            <label
              className="cursor-pointer select-none text-sm text-neutral-700"
              htmlFor="format_on_save"
            >
              Format on Save
            </label>
          </div>
          <div className="flex items-center gap-4 w-full">
            <label className="relative inline-block w-[50px] h-[24px]">
              <input
                className="h-0 w-0 opacity-0"
                type="checkbox"
                checked={intialValues?.auto_save || false}
                onChange={() => {
                  handleChangeInput("auto_save", !intialValues?.auto_save);
                }}
                id="auto_save"
              />
              <button
                onClick={() => {
                  handleChangeInput("auto_save", !intialValues?.auto_save);
                }}
                className="slider round shadow cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
              />
            </label>
            <label
              className="cursor-pointer select-none text-sm text-neutral-700"
              htmlFor="auto_save"
            >
              Auto-Save
            </label>
          </div>

          <div className="mt-1 flex items-start gap-3 w-full">
            <SettingsListbox
              id="font_size"
              label="Font Size"
              value={intialValues?.font_size}
              displayValue={intialValues?.font_size}
              options={FONT_SIZE_OPTIONS}
              onChange={(val) => handleChangeInput("font_size", Number(val))}
            />
            <SettingsListbox
              id="indent"
              label="Indent"
              value={intialValues?.indent}
              displayValue={
                intialValues?.indent === "SPACES" ? "Spaces" : "Tabs"
              }
              options={INDENT_OPTIONS}
              onChange={(val) => handleChangeInput("indent", val)}
            />
            <SettingsListbox
              id="indent_size"
              label="Indent Size"
              value={intialValues?.indent_size}
              displayValue={intialValues?.indent_size}
              options={SPACE_SIZE_OPTIONS}
              onChange={(val) =>
                handleChangeInput("indent_size", Number(val))
              }
            />
          </div>

          <div className="mt-2 flex flex-col items-start w-full">
            {formError && <ErrorBanner title={formError} />}
            <Button tag="button" type="button" onClick={() => toggle()}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default EditorSettingModal;
